/**
 * Returns localValueOverride if not null, otherwise serverValue
 *
 * Useful to get the local value for use cases of the local GraphQL state pattern.
 */
export function getLocalValue<T>(localValueOverride: T | null, serverValue: T): T {
  return localValueOverride ?? serverValue;
}

/**
 * Local GraphQL state pattern where a node has a server field and client field that overrides it if present
 *
 * Returns [localValue, setLocalValue].
 *
 * For example, AnalyticsQueryNode has
 *   server field sqlQuery
 *   client field localSQLQueryOverride
 *
 * The client loads sqlQuery from the server, and locally persists the user's SQL query to
 * localSQLQueryOverride as the user edits it:
 *
 * Initial:
 *   sqlQuery: SELECT 1
 *   localSQLQueryOverride: null
 *   localValue: SELECT 1
 * After call to setLocalValue:
 *   sqlQuery: SELECT 1
 *   localSQLQueryOverride: SELECT * FROM entity (changed)
 *   localValue: SELECT * FROM entity            (changed)
 */
export default function useLocalGraphQLState<T>(
  objectId: string,
  localValueOverride: T | null,
  setLocalValueOverride: (objectId: string, newValue: T | null) => void,
  serverValue: T
): [T, (newValue: T) => void] {
  return [
    getLocalValue(localValueOverride, serverValue),
    (newValue: T) => {
      setLocalValueOverride(objectId, newValue);
    },
  ];
}

/**
 * Local GraphQL state pattern where a node has a server field, client field that overrides it if present, and
 * the client field saves to the server field
 *
 * Returns [localValue, setLocalValue, saveLocalValueToServer].
 * saveLocalValueToServer optionally takes a new value, and defaults to the local value if absent.
 *
 * See useLocalGraphQLState for details about the fields. With this hook, additionally when the user presses
 * save button, then the client persists it to the server.
 *
 * Initial:
 *   sqlQuery: SELECT 1
 *   localSQLQueryOverride: SELECT * FROM entity
 *   localValue: SELECT * FROM entity
 * After call to saveLocalValueToServer:
 *   sqlQuery: SELECT * FROM entity (changed)
 *   localSQLQueryOverride: null    (changed)
 *   localValue: SELECT * FROM entity
 */
export function useSaveableLocalGraphQLState<T, ServerReturnType>(
  objectId: string,
  localValueOverride: T | null,
  setLocalValueOverride: (objectId: string, newValue: T | null) => void,
  serverValue: T,
  setServerValue: (objectId: string, newValue: T) => Promise<ServerReturnType>
): [T, (newValue: T) => void, (newValue?: T) => Promise<ServerReturnType>] {
  const [localValue, setLocalValue] = useLocalGraphQLState(
    objectId,
    localValueOverride,
    setLocalValueOverride,
    serverValue
  );
  return [
    localValue,
    setLocalValue,
    async (newValue?: T): Promise<ServerReturnType> => {
      newValue = newValue ?? localValue;
      const serverReturnValue = await setServerValue(objectId, newValue);
      setLocalValueOverride(objectId, null);
      return serverReturnValue;
    },
  ];
}

/**
 * Hook to use local GraphQL state depending on shouldUseLocalState flag
 *
 * For example, in Analytics, users can change parameter values.
 *   - If writable, then those changes should be persisted to the server. So, read and write server values.
 *   - If read-only, then those changes should only be persisted locally. So, read and write local values
 *     (initialized from the server values).
 */
export function useConditionalLocalGraphQLState<T>(
  shouldUseLocalState: boolean,
  objectId: string,
  localValueOverride: T | null,
  setLocalValueOverride: (objectId: string, newValue: T | null) => void,
  serverValue: T,
  setServerValue: (objectId: string, newValue: T) => void
): [T, (newValue: T) => void] {
  const [localValue, setLocalValue] = useLocalGraphQLState(
    objectId,
    localValueOverride,
    setLocalValueOverride,
    serverValue
  );
  if (shouldUseLocalState) {
    return [localValue, setLocalValue];
  } else {
    return [
      serverValue,
      (newValue: T) => {
        setServerValue(objectId, newValue);
      },
    ];
  }
}
